/**
 * 信息节点组件
 *
 * 使用 LLM 提取结构化信息并存储到临时内存
 * 左侧输入连接点 + 右侧输出连接点
 */

import React, { memo } from 'react';
import type { NodeProps } from '@xyflow/react';
import type { InfoNodeData } from '../../../types/workflow';
import { InputHandle, OutputHandle } from './CommonHandle';
import { useNodeExecutionStatus } from '../../../hooks/useNodeExecutionStatus';
import {
  cn,
  getNodeContainerClasses,
  getNodeHeaderClasses,
} from './nodeStyles';

const InfoNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as InfoNodeData;

  // 使用统一的执行状态 Hook
  const {
    executionStatus,
    isRunning,
    isCompleted,
    isFailed,
  } = useNodeExecutionStatus(id);

  // 提取字段数量
  const fieldCount = nodeData.fields?.length || 0;
  const requiredFieldCount = nodeData.fields?.filter(f => f.required).length || 0;

  // 显示提取字段
  const displayFields = nodeData.fields?.slice(0, 3) || [];
  const hasMoreFields = fieldCount > 3;

  return (
    <div
      className={getNodeContainerClasses('info', executionStatus, selected, isRunning)}
      data-node-id={id}
      data-node-type="info"
      data-execution-status={executionStatus}
    >
      {/* 节点头部 */}
      <div className={getNodeHeaderClasses('info')}>
        {/* 信息图标 */}
        <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium text-cyan-600 flex-1 truncate">
          {nodeData.label || '信息提取'}
        </span>
        {/* 字段数量徽章 */}
        <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded">
          {fieldCount} 字段
        </span>
      </div>

      {/* 节点内容 */}
      <div className="px-3 py-2 space-y-2">
        {/* LLM Prompt 摘要 */}
        <div className={cn(
          'text-sm',
          nodeData.prompt ? 'text-gray-600' : 'text-gray-400 italic',
        )}>
          {nodeData.prompt
            ? nodeData.prompt.length > 40
              ? `${nodeData.prompt.substring(0, 40)}...`
              : nodeData.prompt
            : '请配置提取提示词'}
        </div>

        {/* 提取字段列表 */}
        {fieldCount > 0 && (
          <div className="space-y-1">
            {displayFields.map((field) => (
              <div
                key={field.name}
                className="flex items-center gap-1.5 text-xs text-gray-500"
              >
                {field.required ? (
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                )}
                <span className="font-mono text-cyan-600">{field.name}</span>
                {field.description && (
                  <span className="text-gray-400 truncate">- {field.description}</span>
                )}
              </div>
            ))}
            {hasMoreFields && (
              <div className="text-xs text-gray-400">
                +{fieldCount - 3} 更多字段
              </div>
            )}
          </div>
        )}

        {/* 必填字段提示 */}
        {requiredFieldCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span>{requiredFieldCount} 个必填字段</span>
          </div>
        )}

        {/* 描述 */}
        {nodeData.description && (
          <div className="text-xs text-gray-400">
            {nodeData.description}
          </div>
        )}
      </div>

      {/* 输入连接点（左侧） */}
      <InputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />

      {/* 输出连接点（右侧） */}
      <OutputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />
    </div>
  );
};

InfoNodeComponent.displayName = 'InfoNode';

export const InfoNode = memo(InfoNodeComponent);
export default InfoNode;
